/* eslint-disable max-len */
import React, {useState, useEffect} from 'react';
import {useHistory} from 'react-router-dom';
import {useSelector} from 'react-redux';
import {useTranslation} from 'react-i18next';
import {Helmet} from 'react-helmet';
import styled, {css} from 'styled-components';

import {
    Flex,
    isMobile,
    isTablet,
    mobileMediaQuery,
    notWebMediaQuery,
    tabletMediaQuery,
    webMediaQuery,
} from './../styles';
import {baseURL} from '../services/api';
import {getCurrentLocale} from '../utils/i18n';

import Loader from '../components/Loader';

import webare_bears2 from './../assets/webare_bears2.png';
import on_now_hero from './../assets/on_now_hero.png';
import LiveBox from '../components/LiveBox';
import LeftDecorator from './../assets/reface_left_side_decorator.png';
import RightDecorator from './../assets/reface_right_side_decorator.png';

export const OnNow = props => {
    const {t} = useTranslation();
    const history = useHistory();

    const user = useSelector(state => state.user);
    const config = useSelector(state => state.config);

    const [availableLives, setAvailableLives] = useState(undefined);
    const [decodedToken, setDecodedToken] = useState(undefined);
    const [userLocation, setUserLocation] = useState(undefined);

    const colors = [
        [
            'linear-gradient(160deg, rgba(92,191,234,1) 15%, rgba(92,191,234,1) 15%, rgba(0,155,223,1) 37%, rgba(0,155,223,1) 100%)',
            'rgb(92, 191, 234)',
        ],
        [
            'linear-gradient(160deg, rgba(153,255,255,1) 15%, rgba(153,255,255,1) 15%, rgba(69,251,249,1) 54%, rgba(69,251,249,1) 100%);',
            'rgb(191, 255, 255)',
        ],
        [
            'linear-gradient(160deg, rgba(254,255,148,1) 15%, rgba(254,255,148,1) 15%, rgba(255,234,0,1) 54%, rgba(255,234,0,1) 100%); ',
            'rgb(254, 255, 148)',
        ],
    ];
    let colorCount = 0;

    useEffect(() => {
        userLocation !== undefined && userLocation !== null && loadLives();

        // eslint-disable-next-line
    }, [userLocation]);

    useEffect(() => {
        if (user && user.jwtToken) {
            setDecodedToken(JSON.parse(atob(user.jwtToken.split('.')[1])));
        } else {
            setUserLocation(
                user && user.countryIsoCode ? user.countryIsoCode : null,
            );
        }
    }, [user]);

    useEffect(() => {
        if (decodedToken) {
            setUserLocation(decodedToken.idp_location);
        }

        // eslint-disable-next-line
    }, [decodedToken]);

    const loadLives = async () => {
        let url = `${baseURL}?query=${encodeURI(`
                    {
                        live(language: ${getCurrentLocale().toUpperCase()}, catalog: ${user.catalogTenant ||
            'CNPLUS_LATAM_LATAM'}, platform: "${
            config.catalog_settings && config.catalog_settings.platforms
                ? config.catalog_settings.platforms[0]
                : 'PCTV_DASH'
        }", location: "${userLocation}")
                        {
                            id
                            title
                            description
                        }
                    }`)}`;

        let live_response = await fetch(url)
            .then(res => res.json())
            .catch(err => err);

        if (live_response && live_response.data && live_response.data.live) {
            let lives = live_response.data.live;

            setAvailableLives([...lives]);
        } else {
            setAvailableLives([]);
        }
    };

    return (
        <>
            <Helmet>
                <title>Cartoon Network App</title>
            </Helmet>

            <Container
                direction="column"
                availableLives={availableLives && availableLives.length > 0}
            >
                {availableLives === undefined ? <Loader /> : null}

                {availableLives && availableLives.length === 0 ? (
                    <>
                        <Decoration />
                        <EmptyStateTitle>
                            {t('onnowV2.emptyTitle')}
                        </EmptyStateTitle>
                        <img
                            src={webare_bears2}
                            width={isTablet || isMobile ? '265px' : '450px'}
                            alt="We Bare Bears"
                        />

                        <EmptyMessage>
                            {t('onnowV2.emptySubTitle')}
                        </EmptyMessage>
                    </>
                ) : null}

                {availableLives && availableLives.length > 0 ? (
                    <>
                        <Decoration />
                        <ListTitle>{t('onnowV2.title')}</ListTitle>
                        <ListSubTitle>{t('onnowV2.subtitle')}</ListSubTitle>

                        <ListHero src={on_now_hero} />
                        <Separator />

                        <LivesGrid>
                            {availableLives.map((item, index) => {
                                if (index > 0) {
                                    colorCount++;
                                    colorCount =
                                        colorCount >= colors.length
                                            ? 0
                                            : colorCount;
                                }

                                return (
                                    <LiveBox
                                        key={item.id}
                                        title={
                                            item.liveChannel !== false
                                                ? t('onnowV2.liveTV')
                                                : t('onnowV2.livestream')
                                        }
                                        subtitle={item.title}
                                        footer={t('onnowV2.liveNow')}
                                        background={colors[colorCount][0]}
                                        footerColor={colors[colorCount][1]}
                                        onClick={() =>
                                            history.push(`/now/${item.id}`)
                                        }
                                    />
                                );
                            })}
                        </LivesGrid>
                        <MobileGradientOverlay />
                    </>
                ) : null}
            </Container>
        </>
    );
};

const Container = styled(Flex)`
    padding: ${({availableLives}) =>
        availableLives ? '2rem 40px;' : '132px 2rem 119px 2rem;'};

    ${notWebMediaQuery} {
        margin-top: 78px;
    }

    ${({availableLives}) =>
        availableLives
            ? css`
                  ${mobileMediaQuery} {
                      padding: 2rem 25px;
                  }
              `
            : css`
                  ${tabletMediaQuery} {
                      padding: 76px 0 0 0;
                  }

                  ${mobileMediaQuery} {
                      padding: 42px 16px 0 16px;
                  }
              `};
`;

const EmptyStateTitle = styled.h1`
    margin: 0px;
    font-family: 'TTNormsProExtraBlack';
    font-weight: bold;
    font-size: 24px;
    line-height: 30px;
    text-align: center;
    text-transform: uppercase;
    margin-bottom: 32px;

    ${mobileMediaQuery} {
        font-size: 20px;
        line-height: 26px;
    }
`;

const EmptyMessage = styled.p`
    margin: 0;
    font-family: 'AvenirNextRoundedStd';
    font-size: 18px;
    line-height: 24px;
    text-align: center;
    max-width: 664px;
    margin-top: 32px;

    ${mobileMediaQuery} {
        font-size: 16px;
        line-height: 22px;
    }
`;

const ListTitle = styled.h1`
    margin: 0 0 8px 0;
    font-family: 'TTNormsProExtraBlack';
    font-size: 24px;
    font-weight: bold;
    line-height: 30px;
    text-align: center;
    text-transform: uppercase;

    ${tabletMediaQuery} {
        margin-bottom: 4px;
    }

    ${mobileMediaQuery} {
        margin-bottom: 8px;
        font-size: 20px;
        line-height: 26px;
    }
`;

const ListSubTitle = styled.h2`
    margin: 0 0 30px 0;
    font-family: 'AvenirNextRoundedStd';
    font-size: 18px;
    line-height: 24px;
    font-weight: normal;
    text-align: center;

    ${tabletMediaQuery} {
        margin-bottom: 24px;
    }

    ${mobileMediaQuery} {
        margin-bottom: 16px;
        font-size: 16px;
        line-height: 22px;
    }
`;

const ListHero = styled.img`
    width: 450px;
    height: 160px;
    object-fit: contain;

    ${tabletMediaQuery} {
        width: 350px;
        height: 160px;
    }

    ${mobileMediaQuery} {
        width: 240px;
        height: 110px;
    }
`;

const Separator = styled.hr`
    width: 500px;
    height: 1px;
    border: none;
    background: radial-gradient(
        50% 50% at 50% 50%,
        #ed0080 0%,
        #fde113 53.65%,
        rgba(196, 196, 196, 0) 100%
    );

    margin: 0px;
    opacity: 0.45;

    ${notWebMediaQuery} {
        max-width: 100%;
    }
`;

const LivesGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, 406px);
    width: 100%;
    max-width: 836px;
    padding: 0px;
    margin: 0 auto;
    margin-top: 32px;
    justify-content: center;
    grid-row-gap: 24px;
    grid-column-gap: 24px;

    ${notWebMediaQuery} {
        max-width: 100%;
        box-sizing: border-box;
    }

    ${tabletMediaQuery} {
        margin-top: 32px;
        grid-row-gap: 19px;
        grid-column-gap: 19px;
        grid-template-columns: repeat(2fr, 394px);
    }

    ${mobileMediaQuery} {
        margin-top: 24px;
        grid-row-gap: 16px;
        grid-template-columns: repeat(auto-fit, 100%);
    }
`;

const MobileGradientOverlay = styled.div`
    position: fixed;
    bottom: 0px;
    pointer-events: none;
    background: linear-gradient(
        -180deg,
        rgba(0, 0, 0, 0) 0%,
        rgb(0, 0, 0) 100%
    );
    border-radius: 0px;
    height: 152px;
    width: 100%;

    ${webMediaQuery} {
        display: none;
    }
    ${tabletMediaQuery} {
        display: none;
    }
`;

const Decoration = styled.div`
    position: absolute;
    max-width: 1440px;
    background-image: url(${LeftDecorator}), url(${RightDecorator});
    background-size: contain, contain;
    background-position: left, right;
    background-repeat: no-repeat;
    height: 400px;
    width: 100%;
    z-index: -1;

    ${mobileMediaQuery} {
        height: 300px;
    }
`;

export default OnNow;
